// order-number-genarate.component.ts  ✅ UPDATED: Unified Job Number counter

import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  AsyncValidatorFn
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable, of, Subscription, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, filter, take, startWith } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { OrderNumberService } from '../../services/order-number.service';
import { Router } from '@angular/router';

export interface OrderNumberRecord {
  id?: number;
  userID: number;

  quotationNo: string;
  quotationDate?: Date | string | null;

  piNo: string;
  piDate?: Date | string | null;

  jobNo: string;
  jobDate?: Date | string | null;

  docType: 'D' | 'E' | 'ES';
  isSample?: boolean;

  // Replacement fields
  isReplacement?: boolean;
  replacedFromId?: number | null;

  createdBy?: string;
  editedBy?: string;
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
}

@Component({
  selector: 'app-order-number-generate',
  templateUrl: './order-number-genarate.component.html',
  styleUrls: ['./order-number-genarate.component.scss']
})
export class OrderNumberGenarateComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  records: OrderNumberRecord[] = [];
  displayDialog = false;
  dialogMode: 'create' | 'edit' = 'create';
  selectedRecord: OrderNumberRecord | null = null;
  loading = false;
  recordsLoading = false;
  showSuccessState = false;
  currentLoadingMessage = '';
  saveSuccessMessage = '';
  generatedNumber = '';

  userDetails: any;
  userInitials = '';
  currentYear = new Date().getFullYear();
  activeTab: 'D' | 'E' | 'ES' | 'R' = 'E';
  globalFilterValue = '';
  isHeaderCollapsed = false;

  showSampleSuggestion = false;

  private duplicateCache = new Map<string, boolean>();
  showPasswordModal = true;
  enteredPassword = '';
  correctPassword = 'IS100#';
  passwordLoading = false;
  passwordAttempts = 0;
  maxAttempts = 3;

  // Replacement dialog
  replacementDialogVisible = false;
  replacementLoading = false;
  replacementTarget: OrderNumberRecord | null = null;
  nextReplacementPIPreview = '';
  nextReplacementJobPreview = '';
  replacementForm!: FormGroup;

  private replacementSubs = new Subscription();
  private replacementPreviewSub?: Subscription;

  private replacementPreviewCache: {
    key: string | null;
    wq: string;
    pi: string;
    job: string;
  } = { key: null, wq: '', pi: '', job: '' };

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private authService: AuthService,
    private orderNumberService: OrderNumberService,
    private router: Router
  ) {
    this.form = this.fb.group({
      quotationNo: ['', [Validators.required], [this.createDuplicateValidator('quotationNo')]],
      quotationDate: [new Date()],
      piNo: ['', [], [this.createDuplicateValidator('piNo')]],
      piDate: [null],
      jobNo: ['', [], [this.createDuplicateValidator('jobNo')]],
      jobDate: [null],
      docType: ['E', Validators.required],
      isSample: [false]
    });

    this.replacementForm = this.fb.group({
      replacementOrderNo: ['', Validators.required],
      replacementDate: [new Date(), Validators.required],
      piNo: [''],
      piDate: [{ value: null, disabled: true }],
      jobNo: [''],
      jobDate: [{ value: null, disabled: true }]
    });
  }

  ngOnInit(): void {
    if (!this.authService.getCurrentUser()?.hasAccessToOrderNumbers) {
      this.showPasswordModal = true;
      return;
    }
    this.ngOnInitCore();
  }

  ngOnDestroy(): void {
    this.replacementSubs.unsubscribe();
    this.replacementPreviewSub?.unsubscribe();
  }

  ngOnInitCore(): void {
    this.userDetails = this.authService.getCurrentUser();
    this.userInitials = this.getInitials(this.userDetails?.name || 'Unknown');
    this.loadRecords();

    this.setupReplacementConditionalValidationOnce();

    this.form.get('docType')?.valueChanges.subscribe(() => {
      this.onDocTypeChange();
    });

    this.form.get('isSample')?.valueChanges.subscribe((value) => {
      this.onSampleChange(value);
    });
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  private createDuplicateValidator(field: 'quotationNo' | 'piNo' | 'jobNo'): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value?.trim()) return of(null);

      return of(control.value).pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map(value => {
          const isDuplicate = this.isDuplicateNumber(value, field);
          return isDuplicate ? { duplicate: this.getDuplicateError(field) } : null;
        })
      );
    };
  }

  // ============== PREVIEW GETTERS ==============
  get nextQuotationPreview(): string {
    return this.generateQuotationNumber(this.getNextQuotationCounter());
  }

  get nextSampleQuotationPreview(): string {
    return this.generateSampleQuotationNumber(this.getNextSampleQuotationCounter());
  }

  get nextPIPreview(): string {
    const isSample = this.form.get('isSample')?.value;
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    return this.generatePINumber(this.getNextPICounterInternal(docType, isSample), docType, isSample);
  }

  get nextJobPreview(): string {
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const isSample = this.form.get('isSample')?.value as boolean;
    return this.generateJobNumber(this.getNextJobCounterInternal(docType, isSample), docType, isSample);
  }

  // ============== COUNTER METHODS ==============
  private getNextQuotationCounter(): number {
    let maxNum = 0;
    this.records.forEach(r => {
      const match = r.quotationNo.match(/^SQ-(\d{6})/);
      if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
    });
    return maxNum + 1;
  }

  private getNextSampleQuotationCounter(): number {
    let maxNum = 0;
    this.records.forEach(r => {
      const match = r.quotationNo.match(/^SM-(\d{5})/);
      if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
    });
    return maxNum + 1;
  }

  // ✅ UPDATED: UNIFIED PI Counter (shares sequence across E, S, D)
  private getNextPICounterInternal(docType: 'D' | 'E' | 'ES', isSample: boolean): number {
    const year = this.currentYear.toString();
    let maxNum = 0;

    // Check ALL PI types for current year
    this.records.forEach(r => {
      if (r.piNo) {
        // Match any type: KB/PI/E/2025/001, KB/PI/S/2025/002, KB/PI/D/2025/003
        const match = r.piNo.match(/KB\/PI\/[DES]\/(\d{4})\/(\d+)/);
        if (match && match[1] === year) {
          maxNum = Math.max(maxNum, parseInt(match[2], 10));
        }
      }
    });

    return maxNum + 1;
  }

  // ✅ NEW: SEPARATE Job Counter (each type has own sequence)
  private getNextJobCounterInternal(docType: 'D' | 'E' | 'ES', isSample: boolean): number {
    let maxNum = 0;

    let prefix: string;
    if (docType === 'D') prefix = 'DRW';
    else if (isSample) prefix = 'KBS';
    else prefix = 'KBX';

    this.records.forEach(r => {
      if (r.jobNo) {
        const regex = new RegExp(`^${prefix}-(\\d+)`);
        const match = r.jobNo.match(regex);
        if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
      }
    });

    return maxNum + 1;
  }

  get nextPICounter(): number {
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const isSample = this.form.get('isSample')?.value;
    return this.getNextPICounterInternal(docType, isSample);
  }

  get nextJobCounter(): number {
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const isSample = this.form.get('isSample')?.value;
    return this.getNextJobCounterInternal(docType, isSample);
  }

  // ============== GENERATOR METHODS ==============
  generateQuotationNumber(counter: number): string {
    return `SQ-${String(counter).padStart(6, '0')}-${this.userInitials}`;
  }

  generateSampleQuotationNumber(counter: number): string {
    return `SM-${String(counter).padStart(5, '0')}-${this.userInitials}`;
  }

  generatePINumber(counter: number, docType: 'D' | 'E' | 'ES', isSample: boolean = false): string {
    let typeCode: string;
    if (docType === 'D') typeCode = 'D';
    else if (isSample) typeCode = 'S';
    else typeCode = 'E';
    return `KB/PI/${typeCode}/${this.currentYear}/${String(counter).padStart(3, '0')}`;
  }

  // ✅ UPDATED: Uses unified counter but different prefixes
  generateJobNumber(counter: number, docType: 'D' | 'E' | 'ES', isSample: boolean = false): string {
    let prefix: string;
    if (docType === 'D') prefix = 'DRW';
    else if (isSample) prefix = 'KBS';
    else prefix = 'KBX';
    return `${prefix}-${String(counter).padStart(3, '0')}`;
  }

  // ============== DIALOG METHODS ==============
  openCreateDialog(): void {
    this.dialogMode = 'create';
    this.selectedRecord = null;
    this.showSampleSuggestion = false;

    this.form.reset({
      docType: 'E',
      isSample: false,
      quotationDate: new Date()
    }, { emitEvent: false });

    this.displayDialog = true;

    setTimeout(() => this.suggestAllNumbers(), 100);
  }

  openEditDialog(record: OrderNumberRecord): void {
    if (!this.canEditRecord(record)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Access Denied',
        detail: 'You can only edit your own records',
        life: 3000
      });
      return;
    }

    if (record.isReplacement) {
      this.openEditReplacementDialog(record);
      return;
    }

    this.selectedRecord = { ...record };
    this.dialogMode = 'edit';
    this.showSampleSuggestion = false;

    this.form.patchValue({
      quotationNo: record.quotationNo,
      quotationDate: record.quotationDate ? new Date(record.quotationDate) : null,
      piNo: record.piNo || '',
      piDate: record.piDate ? new Date(record.piDate) : null,
      jobNo: record.jobNo || '',
      jobDate: record.jobDate ? new Date(record.jobDate) : null,
      docType: record.docType || 'E',
      isSample: record.isSample || false
    }, { emitEvent: false });

    this.displayDialog = true;
  }

  // ============== SUGGEST METHODS ==============
  suggestNormalQuotation(): void {
    this.form.patchValue({ quotationNo: this.nextQuotationPreview }, { emitEvent: false });
    this.showSampleSuggestion = false;
    this.messageService.add({
      severity: 'info',
      summary: 'Normal Quotation Selected',
      detail: this.nextQuotationPreview,
      life: 2000
    });
  }

  suggestSampleQuotation(): void {
    this.form.patchValue({ quotationNo: this.nextSampleQuotationPreview }, { emitEvent: false });
    this.showSampleSuggestion = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Sample Quotation Selected',
      detail: this.nextSampleQuotationPreview,
      life: 2000
    });
  }

  suggestPINumber(): void {
    const isSample = this.form.get('isSample')?.value;
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const piNumber = this.generatePINumber(this.nextPICounter, docType, isSample);

    this.form.patchValue({ piNo: piNumber, piDate: new Date() }, { emitEvent: false });

    this.messageService.add({
      severity: 'info',
      summary: 'PI Number Suggested',
      detail: piNumber,
      life: 2000
    });
  }

  suggestJobNumber(): void {
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const isSample = this.form.get('isSample')?.value as boolean;
    const jobNumber = this.generateJobNumber(this.nextJobCounter, docType, isSample);

    this.form.patchValue({ jobNo: jobNumber, jobDate: new Date() }, { emitEvent: false });

    this.messageService.add({
      severity: 'info',
      summary: 'Job Number Suggested',
      detail: jobNumber,
      life: 2000
    });
  }

  suggestAllNumbers(): void {
    const isSample = this.form.get('isSample')?.value;
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';

    if (isSample) {
      this.showSampleSuggestion = true;
    } else {
      this.form.patchValue({
        quotationNo: this.nextQuotationPreview,
        quotationDate: new Date()
      }, { emitEvent: false });
    }

    const piCounter = this.getNextPICounterInternal(docType, isSample);
    const jobCounter = this.getNextJobCounterInternal(docType, isSample);

    this.form.patchValue({
      piNo: this.generatePINumber(piCounter, docType, isSample),
      piDate: new Date(),
      jobNo: this.generateJobNumber(jobCounter, docType, isSample),
      jobDate: new Date()
    }, { emitEvent: false });
  }

  // ============== EVENT HANDLERS ==============
  onDocTypeChange(): void {
    if (this.form.get('docType')?.value === 'D') {
      this.form.patchValue({ isSample: false }, { emitEvent: false });
      this.showSampleSuggestion = false;
    }

    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';
    const isSample = this.form.get('isSample')?.value;

    if (!isSample) {
      this.form.patchValue({
        quotationNo: this.nextQuotationPreview,
        quotationDate: new Date()
      }, { emitEvent: false });
    }

    const piCounter = this.getNextPICounterInternal(docType, isSample);
    const jobCounter = this.getNextJobCounterInternal(docType, isSample);

    this.form.patchValue({
      piNo: this.generatePINumber(piCounter, docType, isSample),
      piDate: new Date(),
      jobNo: this.generateJobNumber(jobCounter, docType, isSample),
      jobDate: new Date()
    }, { emitEvent: false });
  }

  onSampleChange(isSample: boolean): void {
    const docType = this.form.get('docType')?.value as 'D' | 'E' | 'ES';

    if (isSample) {
      this.showSampleSuggestion = true;
      this.form.patchValue({ quotationNo: '' }, { emitEvent: false });
    } else {
      this.showSampleSuggestion = false;
      this.form.patchValue({
        quotationNo: this.nextQuotationPreview,
        quotationDate: new Date()
      }, { emitEvent: false });
    }

    const piCounter = this.getNextPICounterInternal(docType, isSample);
    const jobCounter = this.getNextJobCounterInternal(docType, isSample);

    this.form.patchValue({
      piNo: this.generatePINumber(piCounter, docType, isSample),
      piDate: new Date(),
      jobNo: this.generateJobNumber(jobCounter, docType, isSample),
      jobDate: new Date()
    }, { emitEvent: false });
  }

  // ============== LOAD RECORDS ==============
  loadRecords(): void {
    this.recordsLoading = true;
    this.duplicateCache.clear();

    this.orderNumberService.getRecords().subscribe({
      next: (records: any[]) => {
        this.records = records.map(r => ({
          ...r,
          id: r.id,
          userID: r.userID,
          createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
          updatedAt: r.updatedAt ? new Date(r.updatedAt) : null,
          quotationDate: r.quotationDate ? new Date(r.quotationDate) : null,
          piDate: r.piDate ? new Date(r.piDate) : null,
          jobDate: r.jobDate ? new Date(r.jobDate) : null
        } as OrderNumberRecord));

        this.recordsLoading = false;
      },
      error: (err) => {
        console.error('Load records error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Load Error',
          detail: 'Failed to load records. Please refresh.',
          life: 5000
        });
        this.recordsLoading = false;
      }
    });
  }

  // ============== SAVE RECORD ==============
  async saveRecord(): Promise<void> {
    Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());

    if (this.form.pending) {
      await this.form.statusChanges.pipe(
        filter(status => status !== 'PENDING'),
        take(1)
      ).toPromise();
    }

    if (!this.form.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fix all errors before saving',
        life: 3000
      });
      return;
    }

    if (this.hasDuplicates()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Duplicate Detected',
        detail: 'Please use unique sequence numbers for all fields',
        life: 4000
      });
      return;
    }

    this.loading = true;
    const formData = this.form.value;

    const payload: any = {
      userID: this.userDetails.id,
      quotationNo: formData.quotationNo,
      quotationDate: formData.quotationDate,
      piNo: formData.piNo || '',
      piDate: formData.piDate || null,
      jobNo: formData.jobNo || '',
      jobDate: formData.jobDate || null,
      docType: formData.docType,
      isSample: formData.isSample || false,
      createdBy: this.userDetails?.name || '',
      editedBy: this.dialogMode === 'edit' ? this.userDetails?.name || '' : ''
    };

    const request = this.dialogMode === 'edit' && this.selectedRecord?.id
      ? this.orderNumberService.updateRecord(this.selectedRecord.id, payload)
      : this.orderNumberService.createRecord(payload);

    this.currentLoadingMessage = this.dialogMode === 'edit'
      ? 'Updating Order Number...'
      : 'Generating Order Number...';

    request.subscribe({
      next: (result: any) => {
        const processedRecord: OrderNumberRecord = {
          ...result,
          id: result.id,
          userID: result.userID,
          createdAt: result.createdAt ? new Date(result.createdAt) : new Date(),
          updatedAt: result.updatedAt ? new Date(result.updatedAt) : null,
          quotationDate: result.quotationDate ? new Date(result.quotationDate) : null,
          piDate: result.piDate ? new Date(result.piDate) : null,
          jobDate: result.jobDate ? new Date(result.jobDate) : null
        };

        if (this.dialogMode === 'edit' && this.selectedRecord?.id) {
          const index = this.records.findIndex(r => r.id === this.selectedRecord!.id);
          if (index > -1) this.records[index] = processedRecord;
          this.saveSuccessMessage = '✓ Record updated successfully!';
        } else {
          this.records.unshift(processedRecord);
          this.saveSuccessMessage = '✓ Generate Success';
        }

        this.showSuccessState = true;
        this.generatedNumber = formData.quotationNo;

        setTimeout(() => this.closeDialog(), 1500);
      },
      error: (err) => {
        console.error('Save error:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.dialogMode === 'edit' ? 'Update Failed' : 'Create Failed',
          detail: err.error?.message || 'Operation failed',
          life: 5000
        });
        this.loading = false;
      }
    });
  }

  deleteRecord(record: OrderNumberRecord): void {
    if (!record.id || !this.canEditRecord(record)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Access Denied',
        detail: 'You can only delete your own records',
        life: 3000
      });
      return;
    }

    this.orderNumberService.deleteRecord(record.id).subscribe({
      next: () => {
        this.records = this.records.filter(r => r.id !== record.id);
        this.duplicateCache.clear();
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Record permanently deleted',
          life: 3000
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Delete Error',
          detail: err.error?.message || 'Delete failed',
          life: 5000
        });
      }
    });
  }

  closeDialog(): void {
    this.loading = false;
    this.showSuccessState = false;
    this.displayDialog = false;
    this.selectedRecord = null;
    this.dialogMode = 'create';
    this.showSampleSuggestion = false;
    this.duplicateCache.clear();

    this.form.reset({
      docType: 'E',
      isSample: false,
      quotationDate: new Date()
    }, { emitEvent: false });
  }

  // ============== FILTER METHODS ==============
  get filteredRecords(): OrderNumberRecord[] {
    if (this.activeTab === 'R') {
      return this.records.filter(r => r.docType === 'E' && !!r.isReplacement);
    }
    return this.records.filter(r => r.docType === this.activeTab && !r.isReplacement);
  }

  get drawingCount(): number {
    return this.records.filter(record => record.docType === 'D' && !record.isReplacement).length;
  }

  get encloserCount(): number {
    return this.records.filter(record => record.docType === 'E' && !record.isReplacement && !record.isSample).length;
  }

  get sampleCount(): number {
    return this.records.filter(record => record.docType === 'ES' && !record.isReplacement).length;
  }

  get replacementCount(): number {
    return this.records.filter(r => r.docType === 'E' && !!r.isReplacement).length;
  }

  get filteredAndSearchedRecords(): OrderNumberRecord[] {
    let filtered = this.filteredRecords;

    if (this.globalFilterValue?.trim()) {
      const searchTerm = this.globalFilterValue.toLowerCase().trim();
      filtered = filtered.filter(record =>
        record.quotationNo?.toLowerCase().includes(searchTerm) ||
        record.piNo?.toLowerCase().includes(searchTerm) ||
        record.jobNo?.toLowerCase().includes(searchTerm) ||
        record.createdBy?.toLowerCase().includes(searchTerm) ||
        record.editedBy?.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }

  onGlobalFilterChange(event: any): void {
    this.globalFilterValue = event.target.value;
  }

  clearSearch(): void {
    this.globalFilterValue = '';
  }

  // ============== UTILITY METHODS ==============
  canEditRecord(record: OrderNumberRecord): boolean {
    if (this.userDetails?.role === 'superadmin' || this.userDetails?.employeenumber === '26258') {
      return true;
    }
    return record.userID === this.userDetails?.id;
  }

  wasRecordEdited(record: OrderNumberRecord): boolean {
    if (record.editedBy && record.editedBy.trim()) return true;
    if (!record.updatedAt) return false;
    if (!record.createdAt) return !!record.updatedAt;

    const updatedTime = new Date(record.updatedAt).getTime();
    const createdTime = new Date(record.createdAt).getTime();
    return updatedTime - createdTime > 1000;
  }

  isDuplicateNumber(number: string, field: 'quotationNo' | 'piNo' | 'jobNo'): boolean {
    if (!number?.trim()) return false;

    const cacheKey = `${field}-${number}`;
    if (this.duplicateCache.has(cacheKey)) return this.duplicateCache.get(cacheKey)!;

    const currentRecordId = this.selectedRecord?.id;
    const inputNum = number.trim();

    const isDuplicate = this.records.some(record => {
      if (currentRecordId && record.id === currentRecordId) return false;

      switch (field) {
        case 'quotationNo': {
          const inputMatch = inputNum.match(/^S[QM]-(\d{5,6})/);
          const recordMatch = record.quotationNo?.match(/^S[QM]-(\d{5,6})/);
          return !!(inputMatch && recordMatch && inputMatch[1] === recordMatch[1]);
        }
        case 'piNo':
          return record.piNo?.trim() === inputNum;
        case 'jobNo':
          return record.jobNo?.trim() === inputNum;
      }
      return false;
    });

    this.duplicateCache.set(cacheKey, isDuplicate);
    return isDuplicate;
  }

  getDuplicateError(field: 'quotationNo' | 'piNo' | 'jobNo'): string {
    const number = this.form.get(field)?.value?.trim();
    if (!number || !this.isDuplicateNumber(number, field)) return '';

    switch (field) {
      case 'quotationNo': {
        const seqMatch = number.match(/^S[QM]-(\d{5,6})/);
        return seqMatch ? `Sequence ${seqMatch[1]} already used!` : 'Invalid quotation format';
      }
      case 'piNo':
        return `PI ${number} already used!`;
      case 'jobNo':
        return `Job ${number} already assigned!`;
      default:
        return '';
    }
  }

  hasDuplicates(): boolean {
    return !!this.form.get('quotationNo')?.errors?.['duplicate'] ||
      !!this.form.get('piNo')?.errors?.['duplicate'] ||
      !!this.form.get('jobNo')?.errors?.['duplicate'];
  }

  onHeaderCollapse(isCollapsed: boolean): void {
    this.isHeaderCollapsed = isCollapsed;
  }

  getDocTypeLabel(docType: 'D' | 'E' | 'ES'): string {
    switch (docType) {
      case 'E': return 'Encloser';
      case 'ES': return 'Sample';
      case 'D': return 'Drawing';
      default: return 'Unknown';
    }
  }

  exportToCSV(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'CSV export functionality to be implemented',
      life: 3000
    });
  }

  async verifyPassword(): Promise<void> {
    this.passwordLoading = true;
    await new Promise(resolve => setTimeout(resolve, 800));

    if (this.enteredPassword === this.correctPassword) {
      this.passwordAttempts = 0;
      this.showPasswordModal = false;
      this.passwordLoading = false;
      this.ngOnInitCore();
      return;
    }

    this.passwordAttempts++;
    this.messageService.add({
      severity: 'error',
      summary: `Access Denied (${this.maxAttempts - this.passwordAttempts} attempts left)`,
      detail: 'Incorrect password. Please try again.',
      life: 4000
    });

    this.enteredPassword = '';

    if (this.passwordAttempts >= this.maxAttempts) {
      setTimeout(() => this.redirectToQuotationList(), 2000);
    }

    this.passwordLoading = false;
  }

  cancelAccess(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Access Cancelled',
      detail: 'Redirecting to quotation list...',
      life: 2000
    });
    this.redirectToQuotationList();
  }

  private redirectToQuotationList(): void {
    this.router.navigate(['/quotation-part/quotation-list']);
  }

  // ============== REPLACEMENT METHODS ==============
  canCreateReplacement(record: OrderNumberRecord): boolean {
    return record.docType === 'E' && !record.isSample && !record.isReplacement && !this.hasReplacement(record);
  }

  hasReplacement(record: OrderNumberRecord): boolean {
    return this.records.some(r => r.replacedFromId === record.id);
  }

  private getReplacementCacheKey(mode: 'create' | 'edit', targetId?: number | null): string {
    return `${mode}:${targetId ?? 'none'}:${this.userDetails?.id ?? 'u'}`;
  }

  openReplacementDialog(record: OrderNumberRecord): void {
    if (!this.canCreateReplacement(record)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Allowed',
        detail: 'Replacement is only for Encloser records',
        life: 3000
      });
      return;
    }

    this.replacementTarget = record;
    this.dialogMode = 'create';
    this.replacementDialogVisible = true;
    this.replacementLoading = true;

    const replacementNo = this.generateReplacementQuotationNumber(this.getNextReplacementCounter());

    this.nextReplacementPIPreview = this.getNextReplacementPIPreviewLocal();
    this.nextReplacementJobPreview = this.getNextReplacementJobPreviewLocal();

    this.replacementForm.get('piNo')?.enable({ emitEvent: false });
    this.replacementForm.get('jobNo')?.enable({ emitEvent: false });

    this.replacementForm.reset({
      replacementOrderNo: replacementNo,
      replacementDate: new Date(),
      piNo: '',
      piDate: null,
      jobNo: '',
      jobDate: null
    }, { emitEvent: true });

    this.replacementLoading = false;
  }

  openEditReplacementDialog(record: OrderNumberRecord): void {
    if (!this.canEditRecord(record)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Access Denied',
        detail: 'You can only edit your own replacement records',
        life: 3000
      });
      return;
    }

    this.replacementDialogVisible = true;
    this.replacementLoading = true;
    this.dialogMode = 'edit';

    const originalRecord = this.records.find(r => r.id === record.replacedFromId);
    this.replacementTarget = originalRecord || null;

    this.selectedRecord = { ...record };

    this.replacementForm.get('piNo')?.enable({ emitEvent: false });
    this.replacementForm.get('jobNo')?.enable({ emitEvent: false });

    this.replacementForm.patchValue({
      replacementOrderNo: record.quotationNo,
      replacementDate: record.quotationDate ? new Date(record.quotationDate) : new Date(),
      piNo: record.piNo || '',
      piDate: record.piDate ? new Date(record.piDate) : null,
      jobNo: record.jobNo || '',
      jobDate: record.jobDate ? new Date(record.jobDate) : null
    }, { emitEvent: true });

    const needPi = !record.piNo?.trim();
    const needJob = !record.jobNo?.trim();

    this.nextReplacementPIPreview = needPi ? this.getNextReplacementPIPreviewLocal() : '';
    this.nextReplacementJobPreview = needJob ? this.getNextReplacementJobPreviewLocal() : '';

    this.replacementLoading = false;
  }

  private setupReplacementConditionalValidationOnce(): void {
    this.replacementSubs.unsubscribe();
    this.replacementSubs = new Subscription();

    const piNoCtrl = this.replacementForm.get('piNo');
    const piDateCtrl = this.replacementForm.get('piDate');
    const jobNoCtrl = this.replacementForm.get('jobNo');
    const jobDateCtrl = this.replacementForm.get('jobDate');

    if (!piNoCtrl || !piDateCtrl || !jobNoCtrl || !jobDateCtrl) return;

    this.replacementSubs.add(
      piNoCtrl.valueChanges.pipe(startWith(piNoCtrl.value)).subscribe((v: string) => {
        const hasPi = !!(v && v.trim());
        if (hasPi) {
          piDateCtrl.enable({ emitEvent: false });
          piDateCtrl.setValidators([Validators.required]);
          if (!piDateCtrl.value) piDateCtrl.setValue(new Date(), { emitEvent: false });
        } else {
          piDateCtrl.setValue(null, { emitEvent: false });
          piDateCtrl.clearValidators();
          piDateCtrl.disable({ emitEvent: false });
        }
        piDateCtrl.updateValueAndValidity({ emitEvent: false });
      })
    );

    this.replacementSubs.add(
      jobNoCtrl.valueChanges.pipe(startWith(jobNoCtrl.value)).subscribe((v: string) => {
        const hasJob = !!(v && v.trim());
        if (hasJob) {
          jobDateCtrl.enable({ emitEvent: false });
          jobDateCtrl.setValidators([Validators.required]);
          if (!jobDateCtrl.value) jobDateCtrl.setValue(new Date(), { emitEvent: false });
        } else {
          jobDateCtrl.setValue(null, { emitEvent: false });
          jobDateCtrl.clearValidators();
          jobDateCtrl.disable({ emitEvent: false });
        }
        jobDateCtrl.updateValueAndValidity({ emitEvent: false });
      })
    );
  }

  saveReplacement(): void {
    Object.keys(this.replacementForm.controls).forEach(k => this.replacementForm.get(k)?.markAsTouched());

    if (this.replacementForm.invalid || (!this.replacementTarget?.id && this.dialogMode === 'create')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Please fill all required fields',
        life: 3000
      });
      return;
    }

    this.replacementLoading = true;

    const v = this.replacementForm.getRawValue();

    const payload: any = {
      userID: this.userDetails.id,
      quotationNo: v.replacementOrderNo,
      quotationDate: v.replacementDate,
      docType: 'E',
      isSample: false,
      createdBy: this.dialogMode === 'create'
        ? (this.userDetails?.name || '')
        : this.selectedRecord?.createdBy,
      editedBy: this.dialogMode === 'edit'
        ? (this.userDetails?.name || '')
        : ''
    };

    if (this.dialogMode === 'create') {
      payload.isReplacement = true;
      payload.replacedFromId = this.replacementTarget?.id;
    } else {
      payload.isReplacement = this.selectedRecord?.isReplacement;
      payload.replacedFromId = this.selectedRecord?.replacedFromId;
    }

    if (v.piNo && v.piNo.trim()) {
      payload.piNo = v.piNo.trim();
      payload.piDate = v.piDate;
    } else {
      payload.piNo = '';
      payload.piDate = null;
    }

    if (v.jobNo && v.jobNo.trim()) {
      payload.jobNo = v.jobNo.trim();
      payload.jobDate = v.jobDate;
    } else {
      payload.jobNo = '';
      payload.jobDate = null;
    }

    const request = this.dialogMode === 'edit' && this.selectedRecord?.id
      ? this.orderNumberService.updateRecord(this.selectedRecord.id, payload)
      : this.orderNumberService.createRecord(payload);

    request.subscribe({
      next: (created: any) => {
        const processedRecord: OrderNumberRecord = {
          ...created,
          id: created.id,
          userID: created.userID,
          createdAt: created.createdAt ? new Date(created.createdAt) : new Date(),
          updatedAt: created.updatedAt ? new Date(created.updatedAt) : null,
          quotationDate: created.quotationDate ? new Date(created.quotationDate) : null,
          piDate: created.piDate ? new Date(created.piDate) : null,
          jobDate: created.jobDate ? new Date(created.jobDate) : null
        };

        if (this.dialogMode === 'edit' && this.selectedRecord?.id) {
          const index = this.records.findIndex(r => r.id === this.selectedRecord!.id);
          if (index > -1) this.records[index] = processedRecord;
        } else {
          this.records.unshift(processedRecord);
        }

        this.replacementPreviewCache = { key: null, wq: '', pi: '', job: '' };

        this.messageService.add({
          severity: 'success',
          summary: this.dialogMode === 'edit' ? 'Replacement Updated' : 'Replacement Created',
          detail: created.quotationNo,
          life: 3000
        });

        this.replacementLoading = false;
        this.closeReplacementDialog();
      },
      error: (err) => {
        this.replacementLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.dialogMode === 'edit' ? 'Update Failed' : 'Create Failed',
          detail: err.error?.error || err.error?.message || 'Operation failed',
          life: 4000
        });
      }
    });
  }

  closeReplacementDialog(): void {
    this.replacementDialogVisible = false;
    this.replacementLoading = false;
    this.replacementTarget = null;
    this.selectedRecord = null;
    this.dialogMode = 'create';

    this.replacementPreviewSub?.unsubscribe();
    this.replacementPreviewSub = undefined;

    this.nextReplacementPIPreview = '';
    this.nextReplacementJobPreview = '';

    this.replacementForm.reset({
      replacementDate: new Date(),
      piDate: null,
      jobDate: null
    }, { emitEvent: false });
  }

  suggestReplacementPI(): void {
    if (!this.nextReplacementPIPreview) return;

    this.replacementForm.patchValue({
      piNo: this.nextReplacementPIPreview,
      piDate: new Date()
    }, { emitEvent: true });

    this.messageService.add({
      severity: 'info',
      summary: 'PI Suggested',
      detail: this.nextReplacementPIPreview,
      life: 2000
    });
  }

  suggestReplacementJob(): void {
    if (!this.nextReplacementJobPreview) return;

    this.replacementForm.patchValue({
      jobNo: this.nextReplacementJobPreview,
      jobDate: new Date()
    }, { emitEvent: true });

    this.messageService.add({
      severity: 'info',
      summary: 'Job Suggested',
      detail: this.nextReplacementJobPreview,
      life: 2000
    });
  }

  getOriginalQuotationNo(replacedFromId: number): string {
    const original = this.records.find(r => r.id === replacedFromId);
    return original?.quotationNo || replacedFromId.toString();
  }

  // ======================= REPLACEMENT LOCAL COUNTERS =======================
  private getNextReplacementCounter(): number {
    let max = 0;

    this.records.forEach(r => {
      const m = (r.quotationNo || '').match(/^WQ-(\d{6})-/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });

    return max + 1;
  }

  private generateReplacementQuotationNumber(counter: number): string {
    return `WQ-${String(counter).padStart(6, '0')}-${this.userInitials}`;
  }

  // ✅ Replacement uses same PI logic (E type, not sample) - UNIFIED
  private getNextReplacementPIPreviewLocal(): string {
    const counter = this.getNextPICounterInternal('E', false);
    return this.generatePINumber(counter, 'E', false);
  }

  // ✅ Replacement uses SEPARATE Job counter (E type)
  private getNextReplacementJobPreviewLocal(): string {
    const counter = this.getNextJobCounterInternal('E', false);
    return this.generateJobNumber(counter, 'E', false);
  }

  onReplacementDialogShow(): void {
    if (!this.replacementForm.get('piNo')?.value?.trim()) {
      this.nextReplacementPIPreview = this.getNextReplacementPIPreviewLocal();
    }
    if (!this.replacementForm.get('jobNo')?.value?.trim()) {
      this.nextReplacementJobPreview = this.getNextReplacementJobPreviewLocal();
    }
  }

  onReplacementDialogHide(): void {
    this.nextReplacementPIPreview = '';
    this.nextReplacementJobPreview = '';
  }

  onReplacementPIInput(): void {
    if (!this.replacementForm.get('piNo')?.value?.trim()) {
      this.nextReplacementPIPreview = this.getNextReplacementPIPreviewLocal();
    } else {
      this.nextReplacementPIPreview = '';
    }
  }

  onReplacementJobInput(): void {
    if (!this.replacementForm.get('jobNo')?.value?.trim()) {
      this.nextReplacementJobPreview = this.getNextReplacementJobPreviewLocal();
    } else {
      this.nextReplacementJobPreview = '';
    }
  }
}